// react
import { useState } from 'react';
import styled from 'styled-components';

// components
import { Card, Dialog, Title } from '@/components/atoms';
import { SanityContent } from '@/components/organisms';

// tos
import tos from './terms';
import EditorFields from '@/components/organisms/EditorFields/EditorFields';

interface TOSProps {
  className?: string;
  consent: any;
  setConsent: (consent: any) => void;
  consentFields: any;
}

/**
 * TOS renders the Terms of Service page.
 */
export const TOS = ({ consent, setConsent, consentFields }: TOSProps) => {
  // state
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  return (
    <>
      <Wrapper>
        <Title>Terms of Service</Title>
        <Body>
          <Card elevation={0} isBordered={false} isPaddingSm>
            <Note>
              You can read more about how we handle data safely in our{' '}
              <Link href='https://karehero.com/privacy-policy' target='_blank'>
                privacy policy
              </Link>
              . You will also need to agree to our Terms and Conditions.
              {/* {' '}<ActionLink onClick={() => setIsSummaryOpen(true)}>
                Click here
              </ActionLink>{' '}
              to see a summary. */}
            </Note>
          </Card>
          <StyledCard elevation={0} color={'none'} isPaddingSm>
            <SanityContent content={tos} />
          </StyledCard>
          <Card elevation={0} isBordered={false} color='yellow' isPaddingSm>
            <EditorFields
              fields={consentFields}
              value={consent}
              onChange={(f) => setConsent({ ...consent, ...f })}
            />
          </Card>
        </Body>
      </Wrapper>
      <Dialog
        title='Terms of Service'
        isOpen={isSummaryOpen}
        setIsOpen={setIsSummaryOpen}
      >
        <Summary>
          <p>
            This policy (together with our terms of use and any other documents
            referred to within it) sets out the basis on which any personal data
            we collect from you, or that you provide to us, via our website (
            <Link href='https://www.karehero.com' target='_blank'>
              www.karehero.com
            </Link>
            ), will be processed by us. Please read the following carefully to
            understand our views and practices regarding your personal data and
            how we will treat it.
          </p>
          <p>
            By using our website, you agree to this policy. If you do not agree,
            please do not use our website or submit any personal data through
            it.
          </p>
          <p>
            For the purpose of the Data Protection Act 2018 (the “Act”), the
            data controller is KareHero Limited, a company registered in England
            under the Companies Acts (company number 14314900) having its
            registered office at 128 City Road, London EC1V 2NX. On 25th May
            2018, the Act was superseded by the General Data Protection
            Regulation (“GDPR”). The terms of this policy are applicable under
            the Act and GDPR.
          </p>
          <p>
            Under the GDPR, the lawful basis upon which we process your personal
            data is that such processing is necessary for your legitimate
            interests and necessary for us to provide the services under our
            contract with you.
          </p>
          <p>Information we may collect from you</p>
          <p>We may collect and process the following information about you:</p>
          <ul>
            <li>
              Information that you provide when filling in forms on our websites{' '}
              <Link href='https://www.karehero.com' target='_blank'>
                www.karehero.com
              </Link>
              ,{' '}
              <Link href='https://www.caresourcer.com' target='_blank'>
                www.caresourcer.com
              </Link>
              ;
            </li>
            <li>
              Information provided when using KareHero's applications, products
              and services.
            </li>
            <li>
              If you provide us with a phone number and/or email address, we may
              keep a record of any resulting communications between you and
              KareHero, or between you and any care provider you’ve agreed to
              share those details with;
            </li>
            <li>
              If you contact us by phone, email, social media or otherwise, we
              may keep a record of that correspondence;
            </li>
          </ul>
        </Summary>
      </Dialog>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  gap: ${(props) => props.theme.spacing[16]};
`;

const Note = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

const Link = styled.a`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.primary[70]};
`;

const StyledCard = styled(Card)`
  flex: 1;
  overflow-y: auto;
  background: ${(props) => props.theme.color.neutral[10]};
`;

const Summary = styled.div`
  overflow-y: auto;
`;

TOS.displayName = 'TOS';
export default TOS;
